<template>
  <div>
    <div class="dropdown-menu" :class="{show: $parent.openedDropdownMenu === 'controls'}">
      <h6 class="dropdown-header">{{ $t('UI_LIVE_MODE_TITLE') }}</h6>
      <!-- Init -->
      <a class="dropdown-item pointer" v-if="!$store.getters.instanceInLiveMode" @click="initLiveMode" :class="{'disabled bg-light text-danger font-weight-bold': !canEnableLive}" :disabled="!canEnableLive">
        <i class="fas fa-ban" v-if="!canEnableLive"></i>
        {{ $t('UI_LIVE_MODE_INIT') }}
      </a>
      <!-- Play -->
      <a class="dropdown-item pointer" v-if="canStartTimer" @click="startTimer" :class="{'disabled bg-light text-danger font-weight-bold': !liveStreamOnline}" :disabled="!liveStreamOnline">
        <i class="fas fa-ban" v-if="!liveStreamOnline"></i>
        <i class="fa fa-play" v-else></i> {{ $t('UI_LIVE_MODE_START') }}
      </a>
      <!-- Pause -->
      <a class="dropdown-item pointer" v-if="liveModeRunning" @click="pauseTimer">
        <i class="fa fa-pause"></i> {{ $t('UI_LIVE_MODE_PAUSE') }}
      </a>
      <!-- Stop -->
      <a class="dropdown-item pointer" v-if="liveModeRunning" @click="showStopTimerDialog">
        <i class="fa fa-stop"></i> {{ $t('UI_LIVE_MODE_STOP') }}
      </a>
      <!-- Clear -->
      <a class="dropdown-item pointer" v-if="$store.getters.instanceInLiveMode" @click="showClearLiveDialog">
        <i class="fa fa-sign-out-alt"></i> {{ $t('UI_LIVE_MODE_CLEAR') }}
      </a>
      <div class="dropdown-divider" v-if="!$store.getters.instanceInLiveMode"></div>
      <!-- Import Modal -->
      <a class="dropdown-item pointer" v-if="!$store.getters.instanceInLiveMode"
         @click="$refs.importLastVideoModal.open()">
        <i class="fa fa-file-import"></i> {{ $t('UI_LIVE_MODE_IMPORT') }}
      </a>
    </div>

    <sweet-modal :title="$t('UI_LIVE_MODE_IMPORT')" ref="importLastVideoModal" class="text-left"
                 v-on:open="openImportVideoModal" :blocking="true" :pulse-on-block="false">
      <loading :active.sync="isLoading" :is-full-page="false"></loading>

      <div class="form-group mt-2">
        <label for="settings_tags" class="label">{{ $t("CHANNEL_SETTINGS_MISC_TAGS") }}</label>
        <input type="text" id="settings_tags" v-model="importDefaults.tags" class="form-control">
        <small class="text-muted">{{ $t("CHANNEL_SETTINGS_MISC_TAGS_NOTE") }}</small>
      </div>

      <div class="form-group mt-2">
        <label for="settings_desc" class="label">{{ $t("CHANNEL_SETTINGS_MISC_DESCRIPTION") }}</label>
        <input type="text" id="settings_desc" v-model="importDefaults.description" class="form-control">
      </div>

      {{ $t('UI_LIVE_MODE_IMPORT_MODAL_TEXT') }}
      <v-select :label="$t('CHANNEL_SETTINGS_CATEGORY_SETTINGS_TITLE')"
                :options="importCategories"
                class="mt-2"
                label="name"
                :multiple="true"
                :searchable="true"
                :closeOnSelect="false"
                v-model="selectedCategories"
                :reduce="category => category.id"
                v-on:search:focus="expanded = true"
                v-on:search:blur="expanded = false"
                v-if="importCategories && !isLoading"></v-select>

      <div style="height: 340px;" v-if="expanded"></div>

      <div slot="button">
        <button class="btn btn-primary" @click="importVideo">
          {{ $t('UI_LIVE_MODE_IMPORT_BUTTON') }}
        </button>
        <button class="btn btn-secondary ml-1" @click="$refs.importLastVideoModal.close()">
          {{ $t('UI_MODAL_BUTTON_CLOSE') }}
        </button>
      </div>
    </sweet-modal>


    <sweet-modal :title="$t('UI_LIVE_MODE_STOP')" ref="stopRecordingModal">
      <p>
        {{ $t("UI_LIVE_MODE_STOP_CONFIRMATION_DESCRIPTION") }}
      </p>

      <button class="btn btn-danger" @click="stopTimer">
        {{ $t("UI_LIVE_MODE_STOP")}}
      </button>
      <button class="btn btn-outline-primary ml-2" @click="$refs.stopRecordingModal.close()">
        {{ $t("UI_CLOSE_INSTANCE_DIALOG_ABORT") }}
      </button>
    </sweet-modal>

    <sweet-modal :title="$t('UI_LIVE_MODE_CLEAR')" ref="endLiveModeModal">
      <p>
        {{ $t("UI_LIVE_MODE_CLEAR_CONFIRMATION_DESCRIPTION") }}
      </p>

      <button class="btn btn-danger" @click="clearLiveMode">
        {{ $t("UI_LIVE_MODE_CLEAR")}}
      </button>
      <button class="btn btn-outline-primary ml-2" @click="$refs.endLiveModeModal.close()">
        {{ $t("UI_CLOSE_INSTANCE_DIALOG_ABORT") }}
      </button>
    </sweet-modal>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import { SweetModal } from 'sweet-modal-vue';
import { RepositoryFactory } from '../../../../repositories/RepositoryFactory';

const LiveModeRepository = RepositoryFactory.get('livemode');
const InstanceRepository = RepositoryFactory.get('instances');
const CategoryRepository = RepositoryFactory.get('categories');
const ChannelSettingsRepository = RepositoryFactory.get('channelSettings');

export default {
  name: 'LiveModeControls',
  components: {
    SweetModal,
    Loading,
  },
  data() {
    return {
      importCategories: [],
      selectedCategories: [],
      importDefaults: {
        tags: '',
        description: '',
      },
      isLoading: false,
      expanded: false,
    };
  },
  computed: {
		  liveStreamOnline() {
		    return this.$store.state.liveStreamOnline;
    },
		  canEnableLive() {
		    return this.$store.state.instance.canEnableLive;
    },
    liveModeRunning() {
      if (this.$store.getters.instanceInLiveMode) {
        if (this.$store.getters.activeInstanceData.live !== undefined && this.$store.getters.activeInstanceData.live !== null) {
          return this.$store.getters.activeInstanceData.live.isRunning;
        }
      }
      return false;
    },
    canStartTimer() {
      if (this.$store.getters.instanceInLiveMode) {
        if (this.$store.getters.activeInstanceData.live === undefined) { return false; }

        return this.$store.getters.activeInstanceData.live.isRunning === false;
      }
      return false;
    },
  },
  methods: {
    initLiveMode() {
      // Switch to Livestream Sidepanel Tab
      LiveModeRepository.init()
        .then(() => {
	                this.$store.commit('setActiveSidePanelTab', { position: 'top', tab: 'livestream' });
	                this.$store.commit('setActiveSidePanelTab', { position: 'bottom', tab: 'memberlist' });
        });
    },
    startTimer() {
      LiveModeRepository.start();
    },
    pauseTimer() {
      LiveModeRepository.pause();
    },
    showStopTimerDialog() {
			    this.$refs.stopRecordingModal.open();
    },
    showClearLiveDialog() {
			    this.$refs.endLiveModeModal.open();
    },
    stopTimer() {
      LiveModeRepository.stop()
        .then(() => {
          this.$refs.stopRecordingModal.close();
          this.$snotify.info(this.$t('UI_LIVE_MODE_STOPPED'));
        });
    },
    clearLiveMode() {
      LiveModeRepository.clear()
        .then(() => {
          this.$store.commit('setSelectedEntryId', null);
          this.$store.commit('setSelectedChapterId', null);
          this.$store.commit('setActiveSidePanelTab', { position: 'top', tab: 'video' });
          this.$store.commit('setActiveSidePanelTab', { position: 'bottom', tab: 'memberlist' });
          this.$store.commit('setLiveStreamOnlineStatus', false);

          // Update the Active Instance to load video player
          InstanceRepository.refetchActive(false).then(() => {
            this.$store.commit('setVideoPlayerUrl', this.$store.getters.activeInstanceData.video);
            this.$store.commit('setShouldLoadVideoPlayer', true);
          });

          this.$refs.endLiveModeModal.close();
        });
    },
    openImportVideoModal() {
			  this.isLoading = true;

      CategoryRepository.list()
        .then((res) => {
          this.importCategories = res.data.list;
          this.checkDefaultCategories();
        })
        .catch(err => console.log(err))
        .finally(() => this.isLoading = false);

      ChannelSettingsRepository.getImportDefaults()
        .then((res) => {
          this.importDefaults = res.data.defaults;
        });
    },
    importVideo() {
      this.isLoading = true;
      const data = {
        categories: this.selectedCategories,
        tags: this.importDefaults.tags,
        description: this.importDefaults.description,
      };
      LiveModeRepository.import(data)
        .then((res) => {
          this.$refs.importLastVideoModal.close();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally((res) => {
          this.isLoading = false;
          InstanceRepository.refetchActive(false);
        });
    },
    checkDefaultCategories() {
      this.isLoading = true;
      this.selectedCategories = this.importCategories.filter(category => category.isChannelDefault)
        .map(category => category.id);
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
  .fa {
    font-size: 12px;
  }
</style>
